/**
 * @module Experince/Search-Results
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
/* tslint:disable:no-any */ // Need to support all data types
// Core Libraries
import { Injectable } from '@angular/core';
import { Wove } from 'aspect.js/';
// Services
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';

// Constants
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { SEARCH_RESULT_CONSTANTS } from '@experience/search-results/constant/search-result-constant';

// Models
import { Query } from '@experience/search-results/model/query.model';
import { SearchRequestPayload } from '@experience/search-results/model/search-request-payload.model';

// Interfaces
import {
  SearchResultConfigurableData,
  SearchResultAEMContent,
} from '@core/aem/interfaces/common/global-search-result.interface';
import { PathParams } from '@experience/search-results/interfaces/search-results-payload.interface';

import { SearchResultsResponse } from '@support/api/proxies/responses/api-2132.interface';
import { APIError } from '@support/api/common/error.interface';
import { FilterOptionsSelected, Filter } from '@support/api/common/product.interface';
import { of as observableOf, throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { HttpResponse } from '@angular/common/http';

@Wove({ moduleName: SEARCH_RESULT_CONSTANTS.SEARCH_RESULT_MODULE })
@Injectable()
export class SearchResultService {
  public setFilters: Filter[];
  /**
   * Observable to notify changes for queryModel changes
   */
  public queryModelUpdated$: Observable<boolean>;
  /**
   * Subject to notify changes for queryModel changes
   */
  public queryModelUpdatedSource: Subject<boolean>;

  /**
   *  used to store the value of selected sort
   */
  public selectedsortOptionValue: string;
  /**
   *  used to store the value of selected filter path
   */
  public selectedFilterPath: string;
  /**
   *  used to store the value of selected billing type
   */
  public billingType: string;

  /**
   * Observable to notify changes for filter and sort changes
   */
  public sortFilterUpdated$: Observable<boolean>;
  /**
   * Subject to notify changes for filter and sort changes
   */
  public sortFilterUpdatedSource: Subject<boolean>;
  constructor(
    private apiGateway: ApiGatewayService,
    private contentProviderService: ContentProviderService,
    private objectService: ObjectService,
    private stateStoreUtilsService: StateStoreUtilsService
  ) {
    this.selectedsortOptionValue = SEARCH_RESULT_CONSTANTS.RELEVANCE;
    // Instantiate subject and cast as observable
    this.queryModelUpdatedSource = new Subject();
    this.queryModelUpdated$ = this.queryModelUpdatedSource.asObservable();
    this.sortFilterUpdatedSource = new Subject();
    this.sortFilterUpdated$ = this.sortFilterUpdatedSource.asObservable();
  }

  /**
   * Function returns the query model
   */
  public getQueryModel(): Query {
    return this.stateStoreUtilsService.getGloabalQueryModel();
  }

  /**
   * Function to set Selected Filter Path
   * @param {string} filterPath
   * @memberof SearchResultService
   */
  public setSelectedFilterPath(filterPath: string): void {
    if (
      filterPath === SEARCH_RESULT_CONSTANTS.ASCENDING_PRICE_SORTORDER ||
      filterPath === SEARCH_RESULT_CONSTANTS.DESCENDING_PRICE_SORTORDER
    ) {
      this.selectedFilterPath = '';
    } else {
      this.selectedFilterPath = filterPath;
    }
  }

  /**
   * Function initializes the search model.
   * Only called on page load from search results landing component
   * @param searchTerm
   * @param pageNum
   * @param pageSize
   */
  public initializeSearchQueryModel(searchTerm: string, pageNum: string, pageSize: string): void {
    this.announceQueryModelUpdate(new Query(searchTerm, pageNum, pageSize));
  }

  /**
   * Function to add new filter for searchResults
   * @param category Category
   * @param label Label within category
   */
  public addFilter(category: string, label: string): void {
    const queryModel = this.stateStoreUtilsService.getGloabalQueryModel();
    queryModel.addFilter(category, label);
    this.announceQueryModelUpdate(queryModel);
  }

  /**
   * Function to remove filter for searchResults
   * @param category Category
   * @param label Label within category
   */
  public removeFilter(category: string, label: string): void {
    const queryModel = this.stateStoreUtilsService.getGloabalQueryModel();
    queryModel.removeFilter(category, label);
    this.announceQueryModelUpdate(queryModel);
  }
  /**
   * Function to add sort results for SearchResults
   * @param category Category
   * @param label Label within category
   */

  public addSortOption(sortType: string): void {
    const queryModel = this.stateStoreUtilsService.getGloabalQueryModel();
    queryModel.changeSortType(sortType);
    this.announceQueryModelUpdate(queryModel);
  }

  /**
   * Funciton removes all filters by resetting the query model
   * @returns void
   */
  public removeAllFilters(): void {
    // this.announceQueryModelUpdate(new Query(queryModel.getSearchTerm()));
  }

  /**
   * Function makes API call to retrieve Search results data
   * @returns Observable<object>
   */
  public retrieveSearchResults(): Observable<SearchResultsResponse> {
    const queryModel = this.stateStoreUtilsService.getGloabalQueryModel();

    let pathParams: PathParams = null;
    if (queryModel) {
      const requestBody = new SearchRequestPayload(
        queryModel,
        this.getFiltersRequested(),
        this.getFilterOptionsSelected(),
        this.selectedsortOptionValue,
        this.billingType,
        this.selectedFilterPath
      );
      pathParams = { searchTerm: queryModel.getSearchTerm() };
      return this.apiGateway
        .postRequest(API_MAP.API_2132_globalSearch, requestBody, null, null, pathParams)
        .pipe(
          map((response: HttpResponse<SearchResultsResponse>) => {
            return response.body;
          }),
          catchError((error: APIError) => {
            return observableThrowError(error);
          })
        );
    } else {
      return observableOf(null);
    }
  }

  /**
   * Get AEM Content for Search Results Landing component
   * @returns Observable<object>
   */
  public getSearchResultsAEMData(): Observable<SearchResultAEMContent | object> {
    return this.contentProviderService
      .getContentData(SEARCH_RESULT_CONSTANTS.SEARCH_RESULTS_LANDING_AEM_PATH)
      .pipe(
        map((contentData: object) => {
          const aemContentData = {
            configurableData: null,
            authorableData: null,
          };
          aemContentData.configurableData = <ConfigData>(
            this.contentProviderService.getConfigurableData(contentData)
          );
          aemContentData.authorableData =
            this.contentProviderService.getAuthorableData(contentData);
          return aemContentData;
        })
      );
  }

  /**
   * Function announces changes to queryModel
   * @param queryModel Query
   */
  public announceQueryModelUpdate(queryModel: Query): void {
    this.stateStoreUtilsService.setGloabalQueryModel(queryModel);
    this.queryModelUpdatedSource.next(true);
  }

  /**
   * Check if filters are applied
   * @returns boolean
   */
  public checkFilterApplied(): boolean {
    let isFilterApplied = false;
    if (this.setFilters) {
      for (const filter of this.setFilters) {
        for (const option of filter.filterOptions) {
          if (option.isSelected) {
            isFilterApplied = true;
            break;
          }
        }
      }
    }
    return isFilterApplied;
  }

  /**
   * Get filters  selected
   * @returns string[]
   */
  public getFiltersRequested(): string[] {
    const filters = [];
    if (this.checkFilterApplied()) {
      for (const filter of this.setFilters) {
        for (const option of filter.filterOptions) {
          if (option.isSelected) {
            filters.push(filter.displayName);
            break;
          }
        }
      }
    } else {
      filters.push(SEARCH_RESULT_CONSTANTS.ALL);
    }
    return filters;
  }

  /**
   * Get filter options selected
   * @returns FilterOptionsSelected
   */
  public getFilterOptionsSelected(): FilterOptionsSelected[] {
    const filterOptions = [];
    if (this.checkFilterApplied()) {
      for (const filter of this.setFilters) {
        const selectedFilter = [];
        for (const option of filter.filterOptions) {
          if (option.isSelected) {
            selectedFilter.push(option.displayName);
          }
        }
        if (selectedFilter.length > 0) {
          filterOptions.push({ fieldName: filter.displayName, values: selectedFilter });
        }
      }
    } else {
      filterOptions.push({
        fieldName: 'all',
        values: ['all'],
      });
    }
    return filterOptions;
  }
}
